import React from 'react';
import { Box, TextField, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { OperatingPeriodUtc, ServiceMode } from './types';
import { FEEL_PROFILES } from './feelProfiles';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
import { opsColors } from '../../theme/opsTokens';

interface SummaryRunStepProps {
  servicePreset: string;
  polygonCount: number;
  operatingPeriods: OperatingPeriodUtc[];
  serviceMode: ServiceMode | null;
  demandTrips: number | null;
  vehicleCount: number;
  capacityStandard: number;
  capacityWheelchair: number;
  feelLevel: 1 | 2 | 3 | 4 | 5;
  feelSlope: number;
  feelInterceptMinutes: number;
  maxTripTimeMinutes: number;
  simulationName: string;
  onSimulationNameChange: (value: string) => void;
}

interface SummaryRowModel {
  key: 'preset' | 'service_mode' | 'service_area' | 'operating_hours' | 'trips' | 'vehicles' | 'feel';
  icon: React.ReactNode;
}

const summaryRows: readonly SummaryRowModel[] = [
  { key: 'preset', icon: <Icon name="list-check" size={20} /> },
  { key: 'service_mode', icon: <Icon d={ICON_FALLBACKS.AltRoute} size={20} /> },
  { key: 'service_area', icon: <Icon name="map" size={20} /> },
  { key: 'operating_hours', icon: <Icon name="clock" size={20} /> },
  { key: 'trips', icon: <Icon name="route" size={20} /> },
  { key: 'vehicles', icon: <Icon name="bus" size={20} /> },
  { key: 'feel', icon: <Icon name="sliders" size={20} /> },
];

function getOperatingLabel(periods: OperatingPeriodUtc[]): string {
  return periods.map((period) => `${period.startUtc}-${period.endUtc}`).join(', ');
}

function formatPresetLabel(preset: string): string {
  if (!preset) return '-';
  return preset
    .split('-')
    .map((part) => (part ? `${part[0].toUpperCase()}${part.slice(1).toLowerCase()}` : part))
    .join('-');
}

function formatServiceModeLabel(serviceMode: ServiceMode | null): string {
  if (!serviceMode) return '-';
  return serviceMode
    .split('_')
    .map((part) => (part ? `${part[0].toUpperCase()}${part.slice(1).toLowerCase()}` : part))
    .join(' ');
}

function formatFeelProfileLabel(feelLevel: 1 | 2 | 3 | 4 | 5, t: TFunction): string {
  const fallback = FEEL_PROFILES[feelLevel]?.name;
  return fallback ? t(`serviceFeel.profileNames.${feelLevel}`, { defaultValue: fallback }) : '-';
}

function getSummaryRowValue(row: SummaryRowModel, p: SummaryRunStepProps, t: TFunction): React.ReactNode {
  switch (row.key) {
    case 'preset':
      return formatPresetLabel(p.servicePreset);
    case 'service_mode':
      return formatServiceModeLabel(p.serviceMode);
    case 'service_area':
      return t('summary.zoneCount', { count: p.polygonCount });
    case 'operating_hours':
      return getOperatingLabel(p.operatingPeriods) || '-';
    case 'trips':
      return p.demandTrips != null ? `${p.demandTrips}` : '-';
    case 'vehicles':
      return (
        <Box
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}
        >
          <Box component="span">{p.vehicleCount}</Box>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
            (
            <Icon size={16} name="user" />
            {p.capacityStandard}
            {' / '}
            <Icon size={16} name="wheelchair-move" />
            {p.capacityWheelchair}
            )
          </Box>
        </Box>
      );
    case 'feel':
      return formatFeelProfileLabel(p.feelLevel, t);
    default:
      return '-';
  }
}

export default function SummaryRunStep(props: SummaryRunStepProps) {
  const { t } = useTranslation('onboarding');
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 1.5 }}>
        {t('summary.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t('summary.description')}
      </Typography>

      <Box
        sx={(theme) => ({
          border: `1px solid ${opsColors.neutral.line}`,
          borderRadius: 2,
          backgroundColor: opsColors.neutral.panel,
          p: 1.5,
          mb: 1.5,
        })}
      >
        <TextField
          fullWidth
          label={t('summary.simulationName')}
          value={props.simulationName}
          onChange={(event) => props.onSimulationNameChange(event.target.value)}
          inputProps={{ 'data-testid': 'wizard.summary.simulation-name-input' }}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1, mb: 1.5 }}>
        {summaryRows.map((item) => (
          <Box
            key={item.key}
            sx={(theme) => ({
              border: `1px solid ${opsColors.neutral.line}`,
              borderRadius: 2,
              backgroundColor: opsColors.neutral.panel,
              p: 1.25,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              textAlign: 'center',
            })}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 1, width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, width: '100%' }}>
                <Box sx={{ color: 'actionBrand.primary', display: 'inline-flex', alignItems: 'center' }}>{item.icon}</Box>
                <Typography variant="caption" color="text.secondary">
                  {t(`summary.rows.${item.key}`)}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center', width: '100%' }}>
              {getSummaryRowValue(item, props, t)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
